// @ts-strict-ignore
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Box,
  Button,
  ButtonVariant,
  Modal,
  Text,
} from '@usealloy/component-library';

import InputFields from './InputFields';
import type { InputSection, GapOptions, SizeOptions } from './types';

interface FormModalBodyProps {
  header?: string;
  inputSections: Array<InputSection>;
  isOpen: boolean;
  onSubmit: (payload) => void;
  onClose: () => void;
  primaryButtonLabel: string;
  secondaryButtonLabel: string;
  setCustomInputs: (value: object) => void;
  customInputs: object;
  sectionGap?: GapOptions;
  size?: SizeOptions;
  maxHeight?: number;
  isCustomDirty: boolean;
  customRequiredValid: object;
  disableSubmitButtonInvalid?: boolean;
}

const FormModalBody = ({
  header,
  inputSections,
  isOpen,
  onSubmit,
  onClose,
  primaryButtonLabel,
  secondaryButtonLabel,
  setCustomInputs,
  customInputs,
  sectionGap,
  size,
  isCustomDirty,
  customRequiredValid,
  disableSubmitButtonInvalid = true,
}: FormModalBodyProps) => {
  const {
    register,
    setValue,
    handleSubmit,
    formState: {
      errors,
      isDirty,
      isSubmitting,
      isValid,
      isValidating,
    },
  } = useFormContext();
  const isCustomInvalid = Object.values(customRequiredValid).some(value => value === false);
  const isFormDirty = isDirty || isCustomDirty;
  const PrimaryButton = (
    <Button
      disabled={
        !isFormDirty
        || (!isValid && disableSubmitButtonInvalid)
        || isSubmitting
        || isValidating
        || isCustomInvalid
      }
      onClick={handleSubmit(onSubmit)}
      variant={ButtonVariant.primary}
    >
      {primaryButtonLabel}
    </Button>
  );
  const SecondaryButton = <Button onClick={onClose}>{secondaryButtonLabel}</Button>;

  return (
    <Modal
      size={size}
      header={header}
      isOpen={isOpen}
      onClose={onClose}
      primaryAction={PrimaryButton}
      secondaryAction={SecondaryButton}
    >
      <form>
        <Box gap={sectionGap || 4} alignItems="start" direction="column">
          {inputSections.map(({
            inputs,
            direction,
            description,
            sectionStyle,
            custom,
            CustomComponent,
            customProps,
            customKey,
            inputGap,
          }, idx) => (
            <React.Fragment key={description ?? inputs ? inputs[0]?.label : idx}>
              {description && (<Text>{description}</Text>)}
              <Box
                gap={inputGap || 4}
                alignItems="flex-start"
                justifyContent="flex-start"
                direction={direction || 'column'}
                style={sectionStyle}
              >
                {custom ? (
                  <CustomComponent
                    {...customProps}
                    setCustomInputs={value => setCustomInputs({
                      ...customInputs,
                      [customKey]: value,
                    })}
                  />
                ) : (
                  <InputFields
                    errors={errors}
                    inputs={inputs}
                    register={register}
                    setValue={setValue}
                  />
                )}
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </form>
    </Modal>
  );
};

export default FormModalBody;
